import * as React from 'react';
import { Check, CaretUpDown, Spinner, ArrowLeft, X } from '@phosphor-icons/react';

import { ComboboxItem } from '../combobox';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DivButton } from '@/components/ui/div-button';
export interface TwoLevelComboboxProps {
  firstLevelItems: ComboboxItem[];
  fetchSecondLevelItems: (firstLevelId: string) => Promise<ComboboxItem[]>;
  onSelectionChange?: (firstLevel: ComboboxItem | null, secondLevel: ComboboxItem | null) => void;
  firstLevelLabel?: string;
  secondLevelLabel?: string;
  firstLevelPlaceholder?: string;
  secondLevelPlaceholder?: string;
  className?: string;
  debugMode?: boolean;
  displayLabel?: boolean;
}

export function TwoLevelCombobox({
  firstLevelItems,
  fetchSecondLevelItems,
  onSelectionChange,
  firstLevelLabel = 'First Level',
  secondLevelLabel = 'Second Level',
  firstLevelPlaceholder = 'Select first level...',
  className,
  debugMode = false,
  displayLabel = true,
}: TwoLevelComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<'first' | 'second'>('first');
  const [selectedFirstLevel, setSelectedFirstLevel] = React.useState<ComboboxItem | null>(null);
  const [selectedSecondLevel, setSelectedSecondLevel] = React.useState<ComboboxItem | null>(null);
  const [secondLevelItems, setSecondLevelItems] = React.useState<ComboboxItem[]>([]);
  const [isLoadingSecondLevel, setIsLoadingSecondLevel] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  const handleFirstLevelSelect = async (item: ComboboxItem) => {
    setSelectedFirstLevel(item);
    setSelectedSecondLevel(null); // Reset second level when first level changes
    setIsLoadingSecondLevel(true);
    setView('second');
    setSearchValue(''); // Clear search input when switching to second level

    try {
      const fetchedItems = await fetchSecondLevelItems(item.value);
      setSecondLevelItems(fetchedItems);
    } catch (error) {
      console.warn(error);
      setSecondLevelItems([]);
    } finally {
      setIsLoadingSecondLevel(false);
    }

    onSelectionChange?.(item, null);
  };

  const handleSecondLevelSelect = (item: ComboboxItem) => {
    setSelectedSecondLevel(item);
    setOpen(false);
    onSelectionChange?.(selectedFirstLevel, item);
  };

  const handleBackToFirstLevel = () => {
    setView('first');
    setSearchValue(''); // Clear search input when going back to first level
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && !selectedFirstLevel) {
      setView('first');
    } else if (isOpen && selectedFirstLevel) {
      setView('second');
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the popover
    setSelectedFirstLevel(null);
    setSelectedSecondLevel(null);
    setSecondLevelItems([]);
    setView('first');
    onSelectionChange?.(null, null);
  };

  const getLabel = () => {
    if (selectedSecondLevel) return `${firstLevelLabel} & ${secondLevelLabel}`;
    if (selectedFirstLevel) return `Select ${secondLevelLabel}`;

    return `Select ${firstLevelLabel}`;
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-2">
        {displayLabel && (
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {getLabel()}
          </label>
        )}
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between bg-transparent"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {selectedSecondLevel ? (
                  <div className="truncate flex items-center gap-1">
                    {selectedFirstLevel!.label} → {selectedSecondLevel.label}
                  </div>
                ) : selectedFirstLevel ? (
                  <div className="truncate flex items-center">{selectedFirstLevel.label}</div>
                ) : (
                  <span className="truncate">{firstLevelPlaceholder}</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {(selectedFirstLevel || selectedSecondLevel) && (
                  // Render button as div to prevent button within button error
                  <DivButton
                    variant="ghost"
                    size="sm"
                    className="size-6 p-0 hover:bg-muted rounded-sm"
                    onClick={handleClearSelection}
                    aria-label="Clear selection"
                  >
                    <X className="size-3" />
                  </DivButton>
                )}
                <CaretUpDown className="size-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              {view === 'second' && selectedFirstLevel && (
                <div className="flex items-center gap-2 p-2 border-b">
                  <Button variant="ghost" size="sm" onClick={handleBackToFirstLevel} className="h-8 w-8 p-0 shrink-0">
                    <ArrowLeft className="size-4" />
                  </Button>
                  <div className="text-sm font-medium truncate flex-1 min-w-0 flex items-center">
                    {selectedFirstLevel.label}
                  </div>
                  {/* Render button as div to prevent button within button error */}
                  <DivButton
                    variant="ghost"
                    size="sm"
                    className="size-6 p-0 ml-auto hover:bg-muted rounded-sm shrink-0"
                    onClick={handleClearSelection}
                    aria-label="Clear selection"
                  >
                    <X className="size-3" />
                  </DivButton>
                  {isLoadingSecondLevel && <Spinner className="size-4 animate-spin text-muted-foreground shrink-0" />}
                </div>
              )}

              <CommandInput
                value={searchValue}
                onValueChange={setSearchValue}
                placeholder={
                  view === 'first'
                    ? `Search ${firstLevelLabel.toLowerCase()}...`
                    : `Search ${secondLevelLabel.toLowerCase()}...`
                }
              />
              <CommandList>
                <CommandEmpty>
                  {view === 'first'
                    ? `No ${firstLevelLabel.toLowerCase()} found.`
                    : `No ${secondLevelLabel.toLowerCase()} found.`}
                </CommandEmpty>

                {view === 'first' && (
                  <CommandGroup>
                    {firstLevelItems.map((item) => (
                      <CommandItem key={item.value} value={item.label} onSelect={() => handleFirstLevelSelect(item)}>
                        <span className="flex items-center gap-2">
                          {item.icon}
                          {item.label}
                        </span>
                        <Check
                          className={cn(
                            'ml-2 size-4',
                            selectedFirstLevel?.value === item.value ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {view === 'second' && selectedFirstLevel && (
                  <CommandGroup>
                    {isLoadingSecondLevel ? (
                      <div className="flex items-center justify-center py-6">
                        <Spinner className="size-4 animate-spin text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground">
                          Loading {secondLevelLabel.toLowerCase()}...
                        </span>
                      </div>
                    ) : (
                      secondLevelItems.map((item) => (
                        <CommandItem key={item.value} value={item.label} onSelect={() => handleSecondLevelSelect(item)}>
                          <span className="flex items-center gap-2">
                            {item.icon}
                            {item.label}
                          </span>
                          <Check
                            className={cn(
                              'ml-2 size-4',
                              selectedSecondLevel?.value === item.value ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      ))
                    )}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {(selectedFirstLevel || selectedSecondLevel) && debugMode && (
        <div className="mt-6 p-4 bg-muted/50 rounded-lg space-y-2">
          <h3 className="font-medium text-sm">Selected Values:</h3>
          {selectedFirstLevel && (
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <span className="font-medium">{firstLevelLabel}:</span>
              <div className="flex items-center">{selectedFirstLevel.label}</div>
            </div>
          )}
          {selectedSecondLevel && (
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <span className="font-medium">{secondLevelLabel}:</span>
              <div className="flex items-center">{selectedSecondLevel.label}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
